import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api'
import { useToast } from '../../ui/ToastProvider'
import { useAuth } from '../../auth/AuthContext'
import { Roles } from '../../auth/rbac'
import MenuGuideButton from '../../components/MenuGuideButton'

function unwrap(res) {
  return res?.data?.data ?? res?.data
}

const DOC_CATEGORIES = [
  { id: 'public', label: '서비스 공개 정책', description: '회원가입, 결제, 공개 안내 페이지에서 사용하는 정책 문서' },
  { id: 'codef-consent', label: '진료·숨은보험금 조회 동의', description: 'CODEF 간편인증 기반 조회를 시작하기 전에 확인하는 고객 동의 문서' },
  { id: 'claim-consent', label: '보험금청구 동의', description: '보험금 청구서류 발송 전에 고객 동의와 제공 범위를 확인하는 문서' },
]

const DOC_TYPES = [
  { category: 'public', code: 'TERMS', label: '이용약관', hint: '코드 TERMS · 공개 경로 /terms' },
  { category: 'public', code: 'PRIVACY', label: '개인정보 처리방침', hint: '코드 PRIVACY · /privacy' },
  { category: 'public', code: 'REFUND_POLICY', label: '환불·취소 정책', hint: '코드 REFUND_POLICY · /refund-policy' },
  { category: 'codef-consent', code: 'HEALTH_INQUIRY_PRIVACY', label: '조회 개인정보 동의', hint: '진료정보분석·숨은보험금찾기 공통 동의 문서' },
  { category: 'codef-consent', code: 'HEALTH_INQUIRY_SENSITIVE', label: '민감정보·고유식별정보 동의', hint: '진료정보분석·숨은보험금찾기 공통 동의 문서' },
  { category: 'codef-consent', code: 'HEALTH_INQUIRY_CREDIT', label: '보험·의료비 개인(신용)정보 동의', hint: '숨은보험금찾기 추가 동의 문서' },
  { category: 'codef-consent', code: 'HEALTH_INQUIRY_AGENT_CONFIRM', label: '고객 동의 확인', hint: '진료정보분석·숨은보험금찾기 공통 확인 문서' },
  { category: 'claim-consent', code: 'CLAIM_PRIVACY', label: '청구 개인정보 수집·이용', hint: '보험금청구 발송 전 고객 개인정보 수집·이용 동의 문서' },
  { category: 'claim-consent', code: 'CLAIM_SENSITIVE_UNIQUE_ID', label: '청구 민감정보·고유식별정보', hint: '보험금청구 발송 전 민감정보 및 고유식별정보 처리 동의 문서' },
  { category: 'claim-consent', code: 'CLAIM_THIRD_PARTY_PROVISION', label: '청구 제3자 제공', hint: '보험금청구 발송 전 보험사·전송 대행 등 제3자 제공 동의 문서' },
]

export default function OpsLegalPolicyManagementPage() {
  const { hasAnyRole } = useAuth()
  const isOperator = hasAnyRole ? hasAnyRole([Roles.OPERATOR]) : false
  const { show } = useToast()

  const [docCode, setDocCode] = useState('TERMS')
  const [activeCategory, setActiveCategory] = useState('public')
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(false)

  const [panelOpen, setPanelOpen] = useState(false)
  const [panelMode, setPanelMode] = useState('create')
  const [editingId, setEditingId] = useState(null)
  const [loadedStatus, setLoadedStatus] = useState('')
  const [versionLabel, setVersionLabel] = useState('')
  const [effectiveDate, setEffectiveDate] = useState('')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [saving, setSaving] = useState(false)
  const [loadingPrevious, setLoadingPrevious] = useState(false)

  const selectedMeta = useMemo(() => DOC_TYPES.find((d) => d.code === docCode) || DOC_TYPES[0], [docCode])
  const activeCategoryMeta = useMemo(
    () => DOC_CATEGORIES.find((category) => category.id === activeCategory) || DOC_CATEGORIES[0],
    [activeCategory],
  )
  const visibleDocTypes = useMemo(
    () => DOC_TYPES.filter((d) => d.category === activeCategory),
    [activeCategory],
  )

  const loadList = useCallback(async () => {
    if (!isOperator) return
    setLoading(true)
    try {
      const res = await api.get('/operator/legal-policies', { params: { docCode } })
      const rows = unwrap(res)
      setList(Array.isArray(rows) ? rows : [])
    } catch (e) {
      const msg = e?.response?.data?.message || e.message
      show('목록을 불러오지 못했습니다: ' + msg, 'error')
      setList([])
    } finally {
      setLoading(false)
    }
  }, [docCode, isOperator, show])

  useEffect(() => {
    loadList()
  }, [loadList])

  const openCreate = () => {
    setEditingId(null)
    setPanelMode('create')
    setLoadedStatus('DRAFT')
    setVersionLabel('')
    setEffectiveDate(new Date().toISOString().slice(0, 10))
    setTitle(selectedMeta.label)
    setBody('')
    setPanelOpen(true)
  }

  const selectCategory = (categoryId) => {
    const nextDoc = DOC_TYPES.find((d) => d.category === categoryId)
    setActiveCategory(categoryId)
    if (nextDoc) setDocCode(nextDoc.code)
  }

  const openDocumentPanel = async (id, mode) => {
    setSaving(true)
    try {
      const res = await api.get(`/operator/legal-policies/${id}`)
      const d = unwrap(res)
      setEditingId(id)
      setPanelMode(mode)
      setLoadedStatus(d?.status || '')
      setVersionLabel(d?.versionLabel || '')
      setEffectiveDate(d?.effectiveDate || '')
      setTitle(d?.title || '')
      setBody(d?.body || '')
      setPanelOpen(true)
    } catch (e) {
      const msg = e?.response?.data?.message || e.message
      show('문서를 불러오지 못했습니다: ' + msg, 'error')
    } finally {
      setSaving(false)
    }
  }

  const openRead = (id) => openDocumentPanel(id, 'view')

  const openEdit = (id) => openDocumentPanel(id, 'edit')

  const closePanel = () => {
    setPanelOpen(false)
    setEditingId(null)
    setPanelMode('create')
    setLoadedStatus('')
  }

  const loadPreviousDraft = async () => {
    if (editingId) return
    const source = list.find((row) => row.status === 'PUBLISHED') || list[0]
    if (!source?.id) {
      show('불러올 이전 문서가 없습니다.', 'error')
      return
    }
    if ((title.trim() || body.trim()) && !window.confirm('현재 입력한 제목과 본문을 이전 문서 내용으로 바꿀까요?')) return

    setLoadingPrevious(true)
    try {
      const res = await api.get(`/operator/legal-policies/${source.id}`)
      const d = unwrap(res)
      setTitle(d?.title || selectedMeta.label)
      setBody(d?.body || '')
      show('이전 문서를 불러왔습니다.', 'success')
    } catch (e) {
      const msg = e?.response?.data?.message || e.message
      show('이전 문서를 불러오지 못했습니다: ' + msg, 'error')
    } finally {
      setLoadingPrevious(false)
    }
  }

  const saveDraft = async () => {
    setSaving(true)
    try {
      if (editingId) {
        await api.put(`/operator/legal-policies/${editingId}`, {
          effectiveDate: effectiveDate || null,
          title: title || null,
          body,
        })
        show('초안을 저장했습니다.', 'success')
      } else {
        await api.post('/operator/legal-policies', {
          docCode,
          versionLabel: versionLabel.trim(),
          effectiveDate,
          title: title || null,
          body,
        })
        show('새 초안을 만들었습니다.', 'success')
      }
      closePanel()
      loadList()
    } catch (e) {
      const msg = e?.response?.data?.message || e.message
      show('저장에 실패했습니다: ' + msg, 'error')
    } finally {
      setSaving(false)
    }
  }

  const publishRow = async (id) => {
    if (!window.confirm('이 초안을 즉시 게시합니다. 기존 게시본은 보관(ARCHIVED) 처리됩니다. 계속할까요?')) return
    try {
      await api.post(`/operator/legal-policies/${id}/publish`)
      show('게시했습니다.', 'success')
      loadList()
    } catch (e) {
      const msg = e?.response?.data?.message || e.message
      show('게시에 실패했습니다: ' + msg, 'error')
    }
  }

  const deleteDraft = async (id) => {
    if (!window.confirm('이 초안을 삭제할까요?')) return
    try {
      await api.delete(`/operator/legal-policies/${id}`)
      show('삭제했습니다.', 'success')
      loadList()
    } catch (e) {
      const msg = e?.response?.data?.message || e.message
      show('삭제에 실패했습니다: ' + msg, 'error')
    }
  }

  const readOnlyPanel = panelMode === 'view'
  const panelTitle = readOnlyPanel ? '문서 보기' : editingId ? '초안 편집' : '새 초안'
  const displayStatus = loadedStatus || (editingId ? 'DRAFT' : 'DRAFT')

  if (!isOperator) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-600">
        운영자만 접근할 수 있습니다.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900">정책·약관 관리</h1>
            <MenuGuideButton slug="legal-policy-management" title="정책·약관 관리 가이드" />
          </div>
        </div>
        <Link className="text-sm font-semibold text-brand-navy underline-offset-2 hover:underline" to="/terms">
          공개 이용약관 보기
        </Link>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {DOC_CATEGORIES.map((category) => {
          const selected = activeCategory === category.id
          return (
            <button
              key={category.id}
              type="button"
              onClick={() => selectCategory(category.id)}
              className={`rounded-xl border p-4 text-left transition ${
                selected
                  ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              <div className="text-sm font-bold">{category.label}</div>
              <div className={`mt-1 text-xs ${selected ? 'text-brand-navy/75' : 'text-slate-500'}`}>
                {category.description}
              </div>
            </button>
          )
        })}
      </div>

      <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-3">
        <div className="flex items-center gap-2">
          <div className="h-5 w-1 rounded-full bg-brand-navy" />
          <div className="text-sm font-bold text-slate-900">{activeCategoryMeta.label}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {visibleDocTypes.map((d) => (
            <button
              key={d.code}
              type="button"
              onClick={() => setDocCode(d.code)}
              className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                docCode === d.code ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-slate-500">{selectedMeta.hint}</p>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={openCreate}
          className="rounded-lg bg-teal-700 px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-teal-800"
        >
          새 초안 만들기
        </button>
        <button
          type="button"
          onClick={() => loadList()}
          className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          새로고침
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">버전</th>
              <th className="px-4 py-3">시행일</th>
              <th className="px-4 py-3">상태</th>
              <th className="px-4 py-3">제목</th>
              <th className="px-4 py-3">게시 시각</th>
              <th className="px-4 py-3 text-right">작업</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-4 py-10 text-center text-slate-500" colSpan={6}>
                  불러오는 중…
                </td>
              </tr>
            ) : list.length === 0 ? (
              <tr>
                <td className="px-4 py-10 text-center text-slate-500" colSpan={6}>
                  등록된 초안·게시본이 없습니다. 공개 페이지는 내장 폴백 문구를 보여 줍니다.
                </td>
              </tr>
            ) : (
              list.map((row) => (
                <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50/80">
                  <td className="px-4 py-3 font-mono text-xs text-slate-800">{row.versionLabel}</td>
                  <td className="px-4 py-3 text-slate-700">{row.effectiveDate}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                        row.status === 'PUBLISHED'
                          ? 'bg-emerald-100 text-emerald-900'
                          : row.status === 'DRAFT'
                            ? 'bg-amber-100 text-amber-900'
                            : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {row.status}
                    </span>
                  </td>
                  <td className="max-w-xs truncate px-4 py-3 text-slate-700">{row.title || '—'}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{row.publishedAt || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-wrap justify-end gap-2">
                      <button type="button" className="text-xs font-semibold text-slate-700 hover:underline" onClick={() => openRead(row.id)}>
                        보기
                      </button>
                      {row.status === 'DRAFT' ? (
                        <>
                          <button type="button" className="text-xs font-semibold text-brand-navy hover:underline" onClick={() => openEdit(row.id)}>
                            편집
                          </button>
                          <button type="button" className="text-xs font-semibold text-emerald-700 hover:underline" onClick={() => publishRow(row.id)}>
                            게시
                          </button>
                          <button type="button" className="text-xs font-semibold text-rose-600 hover:underline" onClick={() => deleteDraft(row.id)}>
                            삭제
                          </button>
                        </>
                      ) : (
                        <span className="text-xs text-slate-400">편집 불가</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {panelOpen ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900">{panelTitle}</h2>
                <p className="mt-1 text-xs text-slate-500">
                  {selectedMeta.label} ({docCode})
                  {readOnlyPanel ? ` · ${displayStatus}` : ' · 게시 후에는 수정할 수 없고 새 초안으로 개정합니다.'}
                </p>
              </div>
              <button type="button" className="text-sm font-semibold text-slate-500 hover:text-slate-800" onClick={closePanel}>
                닫기
              </button>
            </div>

            <div className="mt-6 space-y-4">
              {readOnlyPanel ? (
                <>
                  <div className="grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 sm:grid-cols-2">
                    <div>
                      <div className="text-xs font-semibold text-slate-500">버전</div>
                      <div className="mt-1 font-mono font-semibold text-slate-900">{versionLabel || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-slate-500">시행일</div>
                      <div className="mt-1 font-semibold text-slate-900">{effectiveDate || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-slate-500">상태</div>
                      <div className="mt-1 font-semibold text-slate-900">{displayStatus || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-slate-500">문서 코드</div>
                      <div className="mt-1 font-mono font-semibold text-slate-900">{docCode}</div>
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-4">
                    {title ? <h3 className="text-base font-bold text-slate-900">{title}</h3> : null}
                    <div
                      className="mt-4 space-y-4 text-sm leading-relaxed text-slate-800 [&_a]:font-semibold [&_a]:text-brand-navy [&_a]:underline [&_h2]:text-base [&_h2]:font-semibold [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
                      dangerouslySetInnerHTML={{ __html: body }}
                    />
                  </div>
                  <details className="rounded-lg border border-slate-200 bg-slate-50">
                    <summary className="cursor-pointer px-3 py-2 text-sm font-semibold text-slate-700">HTML 원문</summary>
                    <pre className="max-h-72 overflow-auto whitespace-pre-wrap border-t border-slate-200 bg-white p-3 text-xs leading-relaxed text-slate-700">
                      {body || ''}
                    </pre>
                  </details>
                </>
              ) : !editingId ? (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <span className="text-sm font-semibold text-slate-700">새 버전 작성</span>
                    <button
                      type="button"
                      onClick={loadPreviousDraft}
                      disabled={loadingPrevious || list.length === 0}
                      className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {loadingPrevious ? '불러오는 중…' : '이전 초안 불러오기'}
                    </button>
                  </div>
                  <label className="block text-sm font-semibold text-slate-700">
                    버전 식별자
                    <input
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-sm"
                      value={versionLabel}
                      onChange={(e) => setVersionLabel(e.target.value)}
                      placeholder="예: 2026-05-08 또는 v2"
                    />
                  </label>
                </>
              ) : (
                <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
                  버전 <span className="font-mono font-semibold">{versionLabel}</span> (변경 불가)
                </div>
              )}

              {!readOnlyPanel ? (
                <>
                  <label className="block text-sm font-semibold text-slate-700">
                    시행일
                    <input
                      type="date"
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                      value={effectiveDate}
                      onChange={(e) => setEffectiveDate(e.target.value)}
                    />
                  </label>

                  <label className="block text-sm font-semibold text-slate-700">
                    제목 (선택)
                    <input
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="예: 설계 EZ 이용약관"
                    />
                  </label>

                  <label className="block text-sm font-semibold text-slate-700">
                    본문 HTML
                    <textarea
                      className="mt-1 min-h-[280px] w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs leading-relaxed"
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder="<section>...</section>"
                    />
                  </label>
                </>
              ) : null}
              )}
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
              <button type="button" className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700" onClick={closePanel}>
                {readOnlyPanel ? '닫기' : '취소'}
              </button>
              {!readOnlyPanel ? (
                <button
                  type="button"
                  disabled={saving || !effectiveDate || !body.trim() || (!editingId && !versionLabel.trim())}
                  onClick={saveDraft}
                  className="rounded-lg bg-brand-navy px-4 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saving ? '저장 중…' : '초안 저장'}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
